import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { 
  Plus, 
  Wallet, 
  IndianRupee, 
  TrendingUp, 
  History,
  CreditCard,
  Smartphone,
  Building2,
  AlertTriangle,
  X,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const WalletDashboard = () => {
  const [walletBalance, setWalletBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topupAmount, setTopupAmount] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('upi');
  const [isTopupOpen, setIsTopupOpen] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);
  const [showKycModal, setShowKycModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, title: '', message: '' });

  useEffect(() => {
    fetchWalletData();
    fetchTransactions();
    fetchKYCStatus();
    loadRazorpayScript();
  }, []);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const fetchKYCStatus = async () => {
    try {
      const response = await api.get('/kyc/status');
      setKycStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch KYC status:', error);
      setKycStatus(null);
    }
  };

  const fetchWalletData = async () => {
    try {
      const response = await api.get('/payments/wallet/balance');
      setWalletBalance(response.data.data);
    } catch (error) {
      console.error('Error fetching wallet balance:', error);
      toast.error('Failed to load wallet balance');
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await api.get('/payments/wallet/transactions?limit=10');
      setTransactions(response.data.data.transactions);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      // Set empty transactions instead of showing error
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTopup = async () => {
    // Check KYC status first
    if (kycStatus?.status !== 'verified') {
      setShowKycModal(true);
      setIsTopupOpen(false);
      return;
    }

    if (!topupAmount || parseFloat(topupAmount) < 10) {
      toast.error('Minimum top-up amount is ₹10');
      return;
    }

    setProcessing(true);

    try {
      // Create Razorpay order
      const response = await api.post('/payments/create-order', {
        amount: parseFloat(topupAmount),
        currency: 'INR',
        receipt: `wallet_topup_${Date.now()}`,
        notes: {
          purpose: 'wallet_topup',
          user_id: localStorage.getItem('user_id')
        }
      });

      if (response.data) {
        const orderData = response.data;
        
        // Initialize Razorpay payment
        const options = {
          key: orderData.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_your_key_id',
          amount: orderData.amount,
          currency: orderData.currency,
          name: 'XFas Logistics',
          description: 'Wallet Top-up',
          order_id: orderData.order_id,
          handler: async function (response) {
            // Verify payment
            try {
              const verifyResponse = await api.post('/payments/verify', {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              });

              if (verifyResponse.data.status === 'success') {
                // Credit wallet with the payment
                // The server credits what Razorpay actually charged; `amount`
                // is still sent for a backend that has not been updated yet.
                const creditResponse = await api.post('/payments/wallet/credit', {
                  amount: parseFloat(topupAmount),
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_signature: response.razorpay_signature
                });

                if (creditResponse.data.success) {
                  const credited = creditResponse.data.data?.amount_credited ?? parseFloat(topupAmount);
                  toast.success(`₹${Number(credited).toLocaleString('en-IN')} added to wallet successfully!`);
                  setIsTopupOpen(false);
                  setTopupAmount('');
                  fetchWalletData();
                  fetchTransactions();
                } else {
                  toast.error('Failed to credit wallet');
                }
              } else {
                toast.error('Payment verification failed');
              }
            } catch (error) {
              console.error('Payment verification error:', error);
              const detail = error.response?.data?.detail;
              toast.error(typeof detail === 'string' ? detail : 'Failed to verify payment');
            } finally {
              setProcessing(false);
            }
          },
          prefill: {
            name: localStorage.getItem('user_name') || '',
            email: localStorage.getItem('user_email') || '',
            contact: localStorage.getItem('user_phone') || ''
          },
          theme: {
            color: '#2563eb'
          },
          modal: {
            ondismiss: function() {
              toast.error('Payment cancelled');
              setProcessing(false);
            }
          }
        };

        // Close our "Add Money" dialog first. It's a Radix Dialog, which traps
        // focus + pointer events; the Razorpay checkout iframe renders on
        // document.body (outside the dialog), so clicks are swallowed until the
        // user clicks the background and breaks the trap.
        setIsTopupOpen(false);
        const razorpay = new window.Razorpay(options);
        razorpay.open();
      } else {
        toast.error('Failed to create payment order');
        setProcessing(false);
      }
    } catch (error) {
      console.error('Error during top-up:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response detail:', error.response?.data?.detail);
      
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to process top-up';
      
      // Determine error type for better UI
      let errorTitle = 'Payment Error';
      if (error.response?.status === 403) {
        errorTitle = 'Access Denied';
      } else if (error.response?.status === 400) {
        errorTitle = 'Invalid Request';
      } else if (error.response?.status === 500) {
        errorTitle = 'Server Error';
      }
      
      // Close the Add Money dialog
      setIsTopupOpen(false);
      
      // Show beautiful error modal
      setErrorModal({
        show: true,
        title: errorTitle,
        message: errorMessage
      });
      
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type) => {
    // Support both old and new transaction types (case-insensitive)
    const typeUpper = type?.toUpperCase();
    const isCredit = typeUpper === 'CREDIT' || typeUpper === 'WALLET_LOAD' || typeUpper === 'ADMIN_CREDIT';
    
    if (isCredit) {
      return <Plus className="h-4 w-4 text-green-500" />;
    } else {
      return <TrendingUp className="h-4 w-4 text-red-500" />;
    }
  };

  const getTransactionColor = (type) => {
    // Support both old and new transaction types (case-insensitive)
    const typeUpper = type?.toUpperCase();
    const isCredit = typeUpper === 'CREDIT' || typeUpper === 'WALLET_LOAD' || typeUpper === 'ADMIN_CREDIT';
    return isCredit ? 'text-green-600' : 'text-red-600';
  };

  const getTransactionSign = (type) => {
    // Support both old and new transaction types (case-insensitive)
    const typeUpper = type?.toUpperCase();
    const isCredit = typeUpper === 'CREDIT' || typeUpper === 'WALLET_LOAD' || typeUpper === 'ADMIN_CREDIT';
    return isCredit ? '+' : '-';
  };

  const getTransactionBadgeColor = (type) => {
    // Support both old and new transaction types (case-insensitive)
    const typeUpper = type?.toUpperCase();
    const isCredit = typeUpper === 'CREDIT' || typeUpper === 'WALLET_LOAD' || typeUpper === 'ADMIN_CREDIT';
    return isCredit ? 'bg-green-100' : 'bg-red-100';
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'upi':
        return <Smartphone className="h-4 w-4" />;
      case 'credit_card':
      case 'debit_card':
        return <CreditCard className="h-4 w-4" />;
      case 'net_banking':
        return <Building2 className="h-4 w-4" />;
      default:
        return <CreditCard className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Wallet className="h-8 w-8" />
          My Wallet
        </h1>
        <Dialog open={isTopupOpen} onOpenChange={setIsTopupOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 relative">
              <Plus className="h-4 w-4 mr-2" />
              Add Money
              {kycStatus?.status !== 'verified' && (
                <Badge variant="destructive" className="ml-2 text-xs">KYC Required</Badge>
              )}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Add Money to Wallet</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {kycStatus?.status !== 'verified' && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2 text-orange-800">
                    <AlertTriangle className="h-4 w-4" />
                    <p className="text-sm font-medium">KYC verification required to add money</p>
                  </div>
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium mb-2 block">Amount (₹)</label>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={topupAmount}
                  onChange={(e) => setTopupAmount(e.target.value)}
                  min="10"
                />
                <p className="text-xs text-gray-500 mt-1">Minimum amount: ₹10</p>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Payment via Razorpay</label>
                <div className="bg-gray-50 border rounded-lg p-3">
                  <p className="text-sm text-gray-600">
                    Secure payment through Razorpay gateway. All major payment methods supported.
                  </p>
                </div>
              </div>
              
              <Button 
                onClick={handleTopup} 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={!topupAmount || parseFloat(topupAmount) < 10 || processing}
              >
                {processing ? 'Processing...' : `Add ₹${topupAmount || '0'}`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Wallet Balance - Prominent Display */}
      {walletBalance && (
        <>
          {/* Main Balance Card */}
          <Card className="bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 text-white border-0 shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <p className="text-orange-100 text-sm font-medium uppercase tracking-wide">Total Wallet Balance</p>
                  <p className="text-5xl font-bold tracking-tight">{formatCurrency(walletBalance.balance)}</p>
                  <p className="text-orange-100 text-sm">Available for transactions</p>
                </div>
                <div className="bg-white/20 p-4 rounded-full backdrop-blur-sm">
                  <Wallet className="h-12 w-12 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Daily Limit */}
            <Card className="border-2 border-gray-100 hover:border-orange-200 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="text-gray-600 text-sm font-medium">Daily Spending Limit</p>
                    <p className="text-3xl font-bold text-green-600">
                      {formatCurrency(walletBalance.daily_remaining)}
                    </p>
                    <p className="text-xs text-gray-500">
                      of ₹{walletBalance.daily_limit?.toLocaleString('en-IN')} remaining today
                    </p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-full">
                    <TrendingUp className="h-8 w-8 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Wallet Status */}
            <Card className="border-2 border-gray-100 hover:border-orange-200 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="text-gray-600 text-sm font-medium">Wallet Status</p>
                    <div className="flex items-center gap-2 mt-3">
                      <Badge 
                        variant={(walletBalance.is_active && kycStatus?.status === 'verified') ? "default" : "destructive"}
                        className={(walletBalance.is_active && kycStatus?.status === 'verified') ? "bg-green-500 hover:bg-green-600 text-white px-3 py-1" : "px-3 py-1"}
                      >
                        {(walletBalance.is_active && kycStatus?.status === 'verified') ? 'Active' : 'Inactive'}
                      </Badge>
                      {walletBalance.is_frozen && (
                        <Badge variant="destructive" className="px-3 py-1">Frozen</Badge>
                      )}
                      {kycStatus?.status !== 'verified' && (
                        <Badge variant="outline" className="px-3 py-1 border-orange-500 text-orange-700">
                          KYC {kycStatus?.status === 'pending' ? 'Pending' : 'Required'}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Max balance: ₹{walletBalance.max_balance?.toLocaleString('en-IN')}
                    </p>
                    {kycStatus?.status !== 'verified' && (
                      <p className="text-xs text-orange-600 mt-1">
                        Complete KYC to activate wallet
                      </p>
                    )}
                  </div>
                  <div className="bg-blue-50 p-3 rounded-full">
                    <Wallet className="h-8 w-8 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Recent Transactions */}
      <Card className="border-2 border-gray-100">
        <CardHeader className="border-b bg-gray-50">
          <CardTitle className="flex items-center gap-2 text-lg">
            <History className="h-5 w-5 text-orange-600" />
            Recent Transactions
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {transactions.length === 0 ? (
            <div className="text-center py-12">
              <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <History className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium">No transactions yet</p>
              <p className="text-sm text-gray-400 mt-1">Your wallet transactions will appear here</p>
            </div>
          ) : (
            <div className="divide-y">
              {transactions.map((transaction, index) => (
                <div 
                  key={transaction.id} 
                  className={`flex items-center justify-between p-4 hover:bg-gray-50 transition-colors ${
                    index === 0 ? 'bg-orange-50/30' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-full ${getTransactionBadgeColor(transaction.transaction_type)}`}>
                      {getTransactionIcon(transaction.transaction_type)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{transaction.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-sm text-gray-500">
                          {formatDate(transaction.created_at)}
                        </p>
                        {transaction.source && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            transaction.source === 'USER_RAZORPAY' ? 'bg-blue-100 text-blue-700' :
                            transaction.source === 'ADMIN_MANUAL' ? 'bg-purple-100 text-purple-700' :
                            transaction.source === 'BOOKING_PAYMENT' ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {transaction.source === 'USER_RAZORPAY' ? 'Razorpay' :
                             transaction.source === 'ADMIN_MANUAL' ? 'Admin' :
                             transaction.source === 'BOOKING_PAYMENT' ? 'Booking' :
                             transaction.source}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${getTransactionColor(transaction.transaction_type)}`}>
                      {getTransactionSign(transaction.transaction_type)}
                      {formatCurrency(transaction.amount)}
                    </p>
                    <p className="text-xs text-gray-500">
                      Balance: {formatCurrency(transaction.balance_after)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* KYC Required Modal */}
      {showKycModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">KYC Verification Required</h3>
              <button
                onClick={() => setShowKycModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Complete KYC to Add Money</p>
                  <p className="text-sm text-gray-600">Wallet top-up requires verified KYC</p>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-700">
                  To add money to your wallet, you need to complete your KYC verification. 
                  This is required for secure financial transactions.
                </p>
              </div>

              <div className="space-y-2 text-sm text-gray-600">
                <p className="font-medium text-gray-900">Benefits after KYC:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Add money to wallet</li>
                  <li>Use wallet for payments</li>
                  <li>Cash on Delivery (COD) payments</li>
                  <li>Partial payment option</li>
                  <li>Higher transaction limits</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowKycModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setShowKycModal(false);
                  window.location.href = '/profile/kyc';
                }}
                className="flex-1 bg-orange-500 hover:bg-orange-600"
              >
                Complete KYC
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-t-2xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 rounded-full p-2">
                    <XCircle className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold">{errorModal.title}</h3>
                </div>
                <button
                  onClick={() => setErrorModal({ show: false, title: '', message: '' })}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="bg-red-100 rounded-full p-3">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-gray-700 text-base leading-relaxed">
                    {errorModal.message}
                  </p>
                </div>
              </div>

              {/* Action Button */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setErrorModal({ show: false, title: '', message: '' })}
                  className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletDashboard;